import {
  type IntakeSourceRequest,
  IntakeSourceResponseSchema,
} from '@fundline/shared/schemas/imports';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi } from '../../lib/worker-api.ts';

/**
 * Lead sources (M8): webhook sources with an API key, and the saved column mappings of lead vendors
 * (CSV sources). Admins read them through RLS; every change goes through the worker.
 */

export interface IntakeSource {
  id: string;
  name: string;
  kind: 'webhook' | 'csv';
  mapping: Record<string, string>;
  assignmentMode: 'single' | 'round_robin';
  assigneeUserId: string | null;
  roundRobinUserIds: string[];
  defaultTagIds: string[];
  consentSource: string | null;
  consentScope: string | null;
  requireConsentTimestamp: boolean;
  lookupEnabled: boolean;
  keyHint: string | null;
  keyCreatedAt: string | null;
  createdAt: string;
}

export function useIntakeSourcesQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.intakeSources(workspaceId),
    queryFn: async (): Promise<IntakeSource[]> => {
      const { data, error } = await supabase()
        .from('intake_sources')
        .select(
          'id, name, kind, field_mapping, assignment_mode, assignee_user_id, round_robin_user_ids, default_tag_ids, consent_source, consent_scope, require_consent_timestamp, lookup_enabled, api_key_hint, api_key_created_at, created_at',
        )
        .eq('workspace_id', workspaceId)
        .is('archived_at', null)
        .order('name');
      if (error) throw new Error(`Couldn't load lead sources: ${error.message}`);
      return data.map((s) => ({
        id: s.id,
        name: s.name,
        kind: s.kind === 'webhook' ? 'webhook' : 'csv',
        mapping:
          s.field_mapping && typeof s.field_mapping === 'object' && !Array.isArray(s.field_mapping)
            ? Object.fromEntries(
                Object.entries(s.field_mapping).flatMap(([k, v]) =>
                  typeof v === 'string' ? [[k, v]] : [],
                ),
              )
            : {},
        assignmentMode: s.assignment_mode,
        assigneeUserId: s.assignee_user_id,
        roundRobinUserIds: s.round_robin_user_ids,
        defaultTagIds: s.default_tag_ids,
        consentSource: s.consent_source,
        consentScope: s.consent_scope,
        requireConsentTimestamp: s.require_consent_timestamp,
        lookupEnabled: s.lookup_enabled,
        keyHint: s.api_key_hint,
        keyCreatedAt: s.api_key_created_at,
        createdAt: s.created_at,
      }));
    },
  });
}

export interface IntakeEvent {
  id: string;
  status: 'received' | 'processed' | 'rejected' | 'failed';
  outcome: string | null;
  error: string | null;
  merchantId: string | null;
  receivedAt: string;
}

/** The latest leads of a source (stored before processing, so nothing is lost or silent). */
export function useIntakeEventsQuery(workspaceId: string, sourceId: string) {
  return useQuery({
    queryKey: queryKeys.intakeEvents(workspaceId, sourceId),
    queryFn: async (): Promise<IntakeEvent[]> => {
      const { data, error } = await supabase()
        .from('intake_events')
        .select('id, status, outcome, error, merchant_id, received_at')
        .eq('workspace_id', workspaceId)
        .eq('source_id', sourceId)
        .order('received_at', { ascending: false })
        .limit(20);
      if (error) throw new Error(`Couldn't load leads: ${error.message}`);
      return data.map((e) => ({
        id: e.id,
        status: e.status,
        outcome: e.outcome,
        error: e.error,
        merchantId: e.merchant_id,
        receivedAt: e.received_at,
      }));
    },
    refetchInterval: 10_000,
  });
}

export function useIntakeMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources(workspaceId) });
  const base = `/workspaces/${workspaceId}/intake-sources`;

  const create = useMutation({
    mutationFn: (body: IntakeSourceRequest) =>
      workerApi(base, { method: 'POST', body, schema: IntakeSourceResponseSchema }),
    onSettled: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: IntakeSourceRequest }) =>
      workerApi(`${base}/${id}`, { method: 'POST', body, schema: IntakeSourceResponseSchema }),
    onSettled: invalidate,
  });
  const action = useMutation({
    mutationFn: ({ id, act }: { id: string; act: 'rotate-key' | 'revoke-key' | 'archive' }) =>
      workerApi(`${base}/${id}/${act}`, { method: 'POST', schema: IntakeSourceResponseSchema }),
    onSettled: invalidate,
  });
  return { create, update, action };
}
